<template>
  <section class="apple-tv-section">
    <div
      class="tv-slider-container"
      @mouseenter="pauseAutoPlay"
      @mouseleave="resumeAutoPlay"
    >
      <!-- Viewport (overflow hidden, full width) -->
      <div
        class="tv-slider-viewport"
        ref="viewportRef"
        @touchstart="handleTouchStart"
        @touchmove="handleTouchMove"
        @touchend="handleTouchEnd"
      >
        <div
          class="tv-slider-track"
          :class="{ 'no-transition': !enableTransition }"
          :style="trackStyle"
          @transitionend="handleTransitionEnd"
        >
          <div
            v-for="(slide, i) in renderedSlides"
            :key="i"
            class="tv-slide-item"
            :class="{
              active: i === currentIndex,
              'side-slide': i !== currentIndex
            }"
            @click="onSlideClick(i)"
          >
            <!-- Background Artwork -->
            <div class="slide-artwork-layer">
              <img
                :src="slide.image"
                :alt="slide.title"
                class="slide-artwork-img"
                loading="lazy"
              />
              <div class="slide-gradient-scrim"></div>
            </div>

            <!-- Inactive Side Dimmer Overlay -->
            <div class="side-dimmer"></div>

        

            <!-- Bottom Information & Action Bar -->
            <div class="slide-overlay-info">
              <router-link to="/form" class="btn-stream-now" @click.stop>
                <span>{{ t.tvSlider.streamNow }}</span>
                <svg class="play-icon" viewBox="0 0 16 16" width="12" height="12" fill="currentColor">
                  <path d="M4 3.5l8 4.5-8 4.5V3.5z"/>
                </svg>
              </router-link>

              <div class="slide-meta">
                <span class="slide-genre">{{ slide.genre }}</span>
                <span class="meta-separator">•</span>
                <span class="slide-desc">{{ slide.desc }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Bottom Pagination Dots with Active Pill Indicator -->
      <div class="tv-slider-pagination">
        <button
          v-for="(slide, index) in originalSlides"
          :key="'dot-' + slide.id"
          class="pagination-dot"
          :class="{ active: currentActiveRealIndex === index }"
          @click="goToRealSlide(index)"
          :aria-label="`Go to slide ${index + 1}`"
          type="button"
        >
          <span class="dot-indicator">
            <span
              v-if="currentActiveRealIndex === index"
              class="dot-progress"
              :style="{ width: progressPercent + '%' }"
            ></span>
          </span>
        </button>
      </div>
    </div>
  </section>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useI18n } from '@/i18n';

import siloImg from '@/assets/tv-silo.jpg';
import severanceImg from '@/assets/tv-severance.jpg';
import morningImg from '@/assets/tv-morningshow.jpg';
import tedImg from '@/assets/tv-tedlasso.jpg';

export default {
  name: 'AppleTvSlider',
  setup() {
    const { t } = useI18n();

    const originalSlides = computed(() => [
      {
        id: 'silo',
        title: 'Silo',
        image: siloImg,
        genre: t.value.tvSlider.siloGenre,
        desc: t.value.tvSlider.siloDesc
      },
      {
        id: 'severance',
        title: 'Severance',
        image: severanceImg,
        genre: t.value.tvSlider.severanceGenre,
        desc: t.value.tvSlider.severanceDesc
      },
      {
        id: 'morning',
        title: 'The Morning Show',
        image: morningImg,
        genre: t.value.tvSlider.morningGenre,
        desc: t.value.tvSlider.morningDesc
      },
      {
        id: 'tedlasso',
        title: 'Ted Lasso',
        image: tedImg,
        genre: t.value.tvSlider.tedGenre,
        desc: t.value.tvSlider.tedDesc
      }
    ]);

    // 3 repetitions for seamless infinite looping
    const renderedSlides = computed(() => [
      ...originalSlides.value,
      ...originalSlides.value,
      ...originalSlides.value
    ]);

    const numOriginal = computed(() => originalSlides.value.length);
    // Start at middle copy
    const currentIndex = ref(numOriginal.value);
    const enableTransition = ref(true);
    const progressPercent = ref(0);
    const isPaused = ref(false);

    const currentActiveRealIndex = computed(() => {
      const count = numOriginal.value;
      if (!count) return 0;
      return ((currentIndex.value % count) + count) % count;
    });

    let progressInterval = null;
    const slideDuration = 5000;
    const intervalTick = 50;

    const trackStyle = computed(() => {
      // Each slide has width: var(--slide-width), margin: 0 calc(var(--slide-gap) / 2)
      // Translation formula to center active slide:
      return {
        transform: `translateX(calc(50% - (var(--slide-width) / 2) - (var(--slide-gap) / 2) - (${currentIndex.value} * (var(--slide-width) + var(--slide-gap)))))`
      };
    });

    const startTimer = () => {
      stopTimer();
      progressPercent.value = 0;

      progressInterval = setInterval(() => {
        if (!isPaused.value) {
          progressPercent.value += (intervalTick / slideDuration) * 100;
          if (progressPercent.value >= 100) {
            nextSlide();
          }
        }
      }, intervalTick);
    };

    const stopTimer = () => {
      if (progressInterval) clearInterval(progressInterval);
    };

    const nextSlide = () => {
      enableTransition.value = true;
      currentIndex.value++;
      progressPercent.value = 0;
    };

    const prevSlide = () => {
      enableTransition.value = true;
      currentIndex.value--;
      progressPercent.value = 0;
    };

    const goToRealSlide = (realIdx) => {
      enableTransition.value = true;
      currentIndex.value = numOriginal.value + realIdx;
      progressPercent.value = 0;
    };

    const onSlideClick = (clickedIdx) => {
      if (clickedIdx === currentIndex.value) return;
      enableTransition.value = true;
      currentIndex.value = clickedIdx;
      progressPercent.value = 0;
    };

    const handleTransitionEnd = () => {
      const count = numOriginal.value;
      if (currentIndex.value >= count * 2) {
        enableTransition.value = false;
        currentIndex.value -= count;
        nextTick(() => {
          // Re-enable transition on next cycle
          setTimeout(() => {
            enableTransition.value = true;
          }, 30);
        });
      } else if (currentIndex.value < count) {
        enableTransition.value = false;
        currentIndex.value += count;
        nextTick(() => {
          setTimeout(() => {
            enableTransition.value = true;
          }, 30);
        });
      }
    };

    const pauseAutoPlay = () => {
      isPaused.value = true;
    };

    const resumeAutoPlay = () => {
      isPaused.value = false;
    };

    // Touch swipe support
    let touchStartX = 0;
    let touchEndX = 0;

    const handleTouchStart = (e) => {
      touchStartX = e.changedTouches[0].screenX;
      pauseAutoPlay();
    };

    const handleTouchMove = (e) => {
      touchEndX = e.changedTouches[0].screenX;
    };

    const handleTouchEnd = () => {
      const diff = touchStartX - touchEndX;
      if (Math.abs(diff) > 40) {
        if (diff > 0) {
          nextSlide();
        } else {
          prevSlide();
        }
      }
      resumeAutoPlay();
    };

    onMounted(() => {
      startTimer();
    });

    onBeforeUnmount(() => {
      stopTimer();
    });

    return {
      t,
      originalSlides,
      renderedSlides,
      currentIndex,
      currentActiveRealIndex,
      enableTransition,
      progressPercent,
      trackStyle,
      nextSlide,
      prevSlide,
      goToRealSlide,
      onSlideClick,
      handleTransitionEnd,
      pauseAutoPlay,
      resumeAutoPlay,
      handleTouchStart,
      handleTouchMove,
      handleTouchEnd
    };
  }
};
</script>

<style scoped>
.apple-tv-section {
  --slide-width: clamp(320px, 66vw, 960px);
  --slide-gap: 18px;
  width: 100%;
  background-color: #ffffff;
  padding: 10px 0 30px;
  box-sizing: border-box;
  overflow: hidden;
}

.tv-slider-container {
  position: relative;
  width: 100%;
}

/* Viewport with side visibility */
.tv-slider-viewport {
  position: relative;
  width: 100%;
  overflow: hidden;
}

.tv-slider-track {
  display: flex;
  align-items: center;
  transition: transform 0.65s cubic-bezier(0.25, 1, 0.5, 1);
  will-change: transform;
}

.tv-slider-track.no-transition {
  transition: none !important;
}

.tv-slide-item {
  position: relative;
  flex: 0 0 var(--slide-width);
  width: var(--slide-width);
  margin: 0 calc(var(--slide-gap) / 2);
  overflow: hidden;
  background-color: #000000;
  aspect-ratio: 16 / 9;
  max-height: 480px;
  min-height: 280px;
  user-select: none;
  transition: transform 0.55s cubic-bezier(0.25, 1, 0.5, 1),
              opacity 0.55s ease,
              box-shadow 0.55s ease;
  cursor: pointer;
}

/* Active center card */
.tv-slide-item.active {
  opacity: 1;
  cursor: default;
}

/* Side visible cards */
.tv-slide-item.side-slide {
  opacity: 0.5;
}

.tv-slide-item.side-slide:hover {
  opacity: 0.78;
}

/* Side dimmer */
.side-dimmer {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.28);
  z-index: 4;
  opacity: 0;
  transition: opacity 0.4s ease;
  pointer-events: none;
}

.tv-slide-item.side-slide .side-dimmer {
  opacity: 1;
}

/* Artwork Background */
.slide-artwork-layer {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.slide-artwork-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
}

.slide-gradient-scrim {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.2) 0%,
    transparent 35%,
    rgba(0, 0, 0, 0.12) 65%,
    rgba(0, 0, 0, 0.72) 100%
  );
  pointer-events: none;
}

/* Apple TV Logo Top Right */
.tv-badge {
  position: absolute;
  top: 24px;
  right: 28px;
  z-index: 5;
  filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.45));
}

.apple-tv-icon {
  display: block;
}

/* Bottom Action & Meta Bar */
.slide-overlay-info {
  position: absolute;
  bottom: 28px;
  left: 32px;
  right: 32px;
  z-index: 6;
  display: flex;
  align-items: center;
  gap: 18px;
  flex-wrap: wrap;
  transition: opacity 0.4s ease;
}

.tv-slide-item.side-slide .slide-overlay-info {
  opacity: 0;
  pointer-events: none;
}

.btn-stream-now {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: #ffffff;
  color: #000000;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 22px;
  border-radius: 980px;
  text-decoration: none;
  box-shadow: 0 2px 14px rgba(0, 0, 0, 0.24);
  transition: transform 0.2s ease, background-color 0.2s ease;
  white-space: nowrap;
}

.btn-stream-now:hover {
  background: #f5f5f7;
  transform: scale(1.03);
}

.play-icon {
  margin-left: 2px;
}

.slide-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #ffffff;
  font-size: 15px;
  text-shadow: 0 1px 6px rgba(0, 0, 0, 0.65);
}

.slide-genre {
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.01em;
}

.meta-separator {
  opacity: 0.85;
}

.slide-desc {
  font-weight: 400;
  font-size: 18px;
  opacity: 0.95;
}

/* Bottom Pagination */
.tv-slider-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 18px;
}

.pagination-dot {
  background: transparent;
  border: none;
  padding: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dot-indicator {
  position: relative;
  display: block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #d2d2d7;
  overflow: hidden;
  transition: width 0.35s ease, border-radius 0.35s ease, background-color 0.35s ease;
}

.pagination-dot.active .dot-indicator {
  width: 38px;
  height: 8px;
  border-radius: 980px;
  background: #86868b;
}

.dot-progress {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: #1d1d1f;
  border-radius: 980px;
  transition: width 0.05s linear;
}

@media (max-width: 900px) {
  .apple-tv-section {
    --slide-width: clamp(280px, 72vw, 600px);
    --slide-gap: 14px;
    padding: 10px 0 24px;
  }

  .tv-slide-item {
    min-height: 220px;
    max-height: 380px;
    border-radius: 14px;
  }

  .slide-overlay-info {
    bottom: 18px;
    left: 18px;
    right: 18px;
    gap: 12px;
  }

  .slide-meta {
    font-size: 13px;
  }

  .tv-badge {
    top: 16px;
    right: 18px;
  }

  .apple-tv-icon {
    width: 46px;
    height: 20px;
  }
}
</style>
